import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentHYCSFundAccountData,
  type EnrollmentHYCSFundAccountSubmitInput,
  type EnrollmentHYCSFundAccountSubmitOutput,
} from '../../../../models'
import { createMutationHook, createQueryHook } from '../../../../hooks'

/**
 * Get Enrollment HYCS FundAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFundAccountData>
 * @example const response = await getEnrollmentHYCSFundAccountData()
 */
export const getEnrollmentHYCSFundAccountData = async (
  context?: DefaultContext,
): Promise<EnrollmentHYCSFundAccountData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentHYCSFundAccountData',
      query: `mutation getEnrollmentHYCSFundAccountData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              applicationId
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFundAccountData }
  return response.submitEnrollment
}

export const useGetEnrollmentHYCSFundAccountData = createQueryHook<undefined, EnrollmentHYCSFundAccountData>(
  getEnrollmentHYCSFundAccountData,
)

/**
 * Get Plaid Linking Token
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFundAccountSubmitOutput>
 * @example const response = await getEnrollmentyHYCSFundAccountPlaidToken();
 */
export const getEnrollmentyHYCSPlaidToken = async (
  enrollmentFundAccountSubmitInput: EnrollmentHYCSFundAccountSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSFundAccountSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentyHYCSPlaidToken',
      query: `mutation getEnrollmentyHYCSPlaidToken($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            enrollmentData {
              plaidLinkToken
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFundAccountSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFundAccountSubmitOutput }
  return response.submitEnrollment
}

export const useGetHYCSEnrollmentPlaidToken = createMutationHook<
  EnrollmentHYCSFundAccountSubmitInput,
  EnrollmentHYCSFundAccountSubmitOutput
>(getEnrollmentyHYCSPlaidToken)

/**
 * Submit Enrollment HYCS FundAccount data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentHYCSFundAccountSubmitOutput>
 * @example const response = await submitEnrollmentHYCSFundAccountData();
 */
export const submitEnrollmentHYCSFundAccountData = async (
  enrollmentFundAccountSubmitInput: EnrollmentHYCSFundAccountSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentHYCSFundAccountSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentHYCSFundAccountData',
      query: `mutation submitEnrollmentHYCSFundAccountData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            complete
            enrollmentData {
              accountLinked
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
            type
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFundAccountSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentHYCSFundAccountSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentHYCSFundAccountData = createMutationHook<
  EnrollmentHYCSFundAccountSubmitInput,
  EnrollmentHYCSFundAccountSubmitOutput
>(submitEnrollmentHYCSFundAccountData)
